import React, { Component } from 'react'
import { string, any, func, object, bool, oneOf, oneOfType } from 'prop-types'
import classNames from 'classnames'

import './Input.css'

const INPUT_CONSTANTS = {
  TYPE_TEXT: 'text',
  ACTIVE_CLASS_NAME: 'active',
  SPACE_KEY_CODE: 32,
  INPUT_CLASS_NAME: 'input-text',
  INPUT_CLASS_LABEL: 'input-text__label',
  INPUT_BIG: 'input-text-big',
  INPUT_CLASS_BIG: 'input-text-big',
  INPUT_CLASS_BIG_LABEL: 'input-big__label',
  MESSAGE_CLASS_NAME: 'input-text__message',
  EMPTY_CLASS_NAME: '',
}

const SIZE = {
  BIG: 'big',
  DEFAULT: 'default',
}

class Input extends Component {
  state = {
    isActive: false,
    shouldShowValidationFeedback: true,
  }

  onFocus = (event) => {
    this.setState({
      isActive: true,
      shouldShowValidationFeedback: false,
    })

    this.props.onFocus && this.props.onFocus(event)
  }

  onBlur = (event) => {
    const { value, onBlur } = this.props
    const hasValue = !!value

    this.setState({
      isActive: hasValue,
      shouldShowValidationFeedback: true,
    })

    onBlur && onBlur(event)
  }

  onChange = (event) => {
    this.setState({ shouldShowValidationFeedback: false })

    this.props.onChange && this.props.onChange(event)
  }

  onKeyDown = (event) => {
    const { value } = event.target
    if (value.length === 0 && this.isSpaceKey(event.keyCode)) {
      return event.preventDefault()
    }
  }

  isSpaceKey = (keyCode) => {
    return keyCode === INPUT_CONSTANTS.SPACE_KEY_CODE
  }

  getClassNameByStatus = () => {
    const { ACTIVE_CLASS_NAME, EMPTY_CLASS_NAME } = INPUT_CONSTANTS
    const isAlreadyActiveOrHasValue = this.state.isActive || !!this.props.value

    if (!isAlreadyActiveOrHasValue) {
      return EMPTY_CLASS_NAME
    }

    return ACTIVE_CLASS_NAME
  }

  buildErrorClassName = (currentClassName) => {
    if (!this.shouldShowError()) {
      return INPUT_CONSTANTS.EMPTY_CLASS_NAME
    }

    return `${currentClassName}--${this.props.validation.type}`
  }

  shouldShowError = () => {
    const { validation } = this.props
    const hasValidationMessage = validation && validation.message

    return hasValidationMessage && this.state.shouldShowValidationFeedback
  }

  getClassName = () => {
    const isBig = this.props.size === SIZE.BIG

    return classNames(
      INPUT_CONSTANTS.INPUT_CLASS_NAME,
      { [INPUT_CONSTANTS.INPUT_CLASS_BIG]: isBig },
      this.buildErrorClassName(INPUT_CONSTANTS.INPUT_CLASS_NAME),
    )
  }

  getLabelClassName = () => {
    return classNames(
      INPUT_CONSTANTS.INPUT_CLASS_LABEL,
      this.getClassNameByStatus(),
    )
  }

  getErrorClassName = () => {
    const { MESSAGE_CLASS_NAME } = INPUT_CONSTANTS
    return `${MESSAGE_CLASS_NAME} ${this.buildErrorClassName(
      MESSAGE_CLASS_NAME,
    )}`
  }

  render = () => {
    const {
      validation,
      inputId,
      type,
      value,
      onClick,
      name,
      maxLength,
      reference,
      autoComplete,
      label,
      autoFocus,
      tabIndex,
    } = this.props

    return (
      <div className={this.getClassName()}>
        <label className={this.getLabelClassName()}>{label}</label>

        <input
          autoFocus={autoFocus}
          type={type}
          id={inputId}
          aria-label={label}
          name={name}
          value={value}
          maxLength={maxLength}
          ref={reference}
          autoComplete={autoComplete}
          onClick={onClick}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          tabIndex={tabIndex}
        />

        {this.shouldShowError() && (
          <p className={this.getErrorClassName()}>{validation.message}</p>
        )}
      </div>
    )
  }
}

Input.propTypes = {
  inputId: string,
  type: string,
  size: oneOf([SIZE.BIG, SIZE.DEFAULT]),
  label: string,
  value: any,
  onChange: func,
  onClick: func,
  onBlur: func,
  validation: object,
  name: string,
  maxLength: string,
  reference: oneOfType([func, object]),
  autoFocus: bool,
  autoComplete: oneOf([
    'off',
    'on',
    'new-password',
    'current-password',
    'email',
  ]),
  onFocus: func,
  tabIndex: string,
}

Input.defaultProps = {
  type: INPUT_CONSTANTS.TYPE_TEXT,
  value: '',
  autoFocus: false,
  autoComplete: 'on',
}

export { Input }
